import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Chip,
  Stack,
  Grid,
} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { ThaiStar, ThaiNestedMotif, ThaiDivider } from './ThaiMotif';

interface ProjectItem {
  title: string;
  subtitle: string;
  description: string[];
  tags: string[];
  icon: React.ReactNode;
  accentColor: string;
}

const projects: ProjectItem[] = [
  {
    title: 'NLP of Transformer-based Models on Health Informatics Data',
    subtitle: 'University of Sydney',
    description: [
      'Pre-trained and fine-tuned BERT/ALBERT models on biomedical data',
      'Achieved state-of-the-art performance on health informatics benchmarks',
      'Research focused on transfer learning for domain-specific NLP tasks',
    ],
    tags: ['NLP', 'BERT', 'ALBERT', 'PyTorch', 'Biomedical', 'Deep Learning'],
    icon: <PsychologyIcon sx={{ fontSize: 28 }} />,
    accentColor: '#C8A24D',
  },
  {
    title: 'Microphone Array Spatial Noise Reduction',
    subtitle: 'University of New South Wales',
    description: [
      'Compared DSB, MVDR, ICA, and DUET techniques for source separation',
      'Tested across various acoustic environments',
      'Research on signal processing and spatial audio techniques',
    ],
    tags: ['Signal Processing', 'DSP', 'MATLAB', 'Research', 'Audio Engineering'],
    icon: <ScienceIcon sx={{ fontSize: 28 }} />,
    accentColor: '#5C3D2E',
  },
];

const Projects: React.FC = () => {
  return (
    <Box
      id="projects"
      sx={{
        py: { xs: 10, md: 14 },
        background: '#FFFFFF',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Thai motif decorations */}
      <ThaiNestedMotif size={110} opacity={0.045} innerColor="#FFFFFF" sx={{ bottom: '6%', right: '3%' }} />
      <ThaiStar size={65} opacity={0.035} rotate={25} sx={{ top: '10%', left: '3%' }} />
      <ThaiStar size={40} opacity={0.025} rotate={-10} sx={{ top: '60%', right: '6%' }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="overline"
            sx={{
              color: '#C8A24D',
              fontWeight: 600,
              letterSpacing: 3,
              fontSize: '0.8rem',
              mb: 1.5,
              display: 'block',
            }}
          >
            RESEARCH PROJECTS
          </Typography>
          <Typography
            variant="h2"
            sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#2A231C' }}
          >
            University{' '}
            <Box component="span" className="serif-heading" sx={{ color: '#C8A24D' }}>
              Research
            </Box>
          </Typography>
          <ThaiDivider />
        </Box>

        <Grid container spacing={3}>
          {projects.map((project, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={index}>
              <Card
                className="card"
                sx={{
                  p: 0,
                  height: '100%',
                  overflow: 'hidden',
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    p: 3,
                    borderBottom: '1px solid rgba(200, 162, 77, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '10px',
                      background: `${project.accentColor}10`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: project.accentColor,
                    }}
                  >
                    {project.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: '#2A231C',
                        lineHeight: 1.3,
                      }}
                    >
                      {project.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#9CA3AF',
                        fontSize: '0.85rem',
                        mt: 0.3,
                      }}
                    >
                      {project.subtitle}
                    </Typography>
                  </Box>
                </Box>

                {/* Content */}
                <Box sx={{ p: 3 }}>
                  <Stack spacing={1} sx={{ mb: 3 }}>
                    {project.description.map((desc, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 5,
                            height: 5,
                            borderRadius: '50%',
                            background: project.accentColor,
                            mt: 0.9,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: '#7A6E60', fontSize: '0.88rem' }}
                        >
                          {desc}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>

                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {project.tags.map((tag, i) => (
                      <Chip
                        key={i}
                        label={tag}
                        size="small"
                        sx={{
                          background: 'rgba(92, 61, 46, 0.06)',
                          color: '#7A6E60',
                          border: '1px solid rgba(92, 61, 46, 0.1)',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Projects;
